import * as React from "react";
import { ArrowRight, CheckIcon, PlayIcon, PauseIcon, StepForwardIcon } from "lucide-react";
import { icons as lucide } from "lucide-react";

import * as ArrayUtils from "Utils/ArrayUtils";
import * as StyleUtils from "Utils/StyleUtils";
import * as TelemetryUtils from "TelemetryUtils";
import { Footer } from "Footer";
import AWSLogo from "../images/aws_logo.svg";
import AzureLogo from "../images/azure_logo.svg";
import TrieveLogo from "../images/trieve_logo.png";
import AdobeLogo from "../images/adobe_logo.svg";
import HingeHealthLogo from "../images/hinge_health_logo.svg";
import CreaLogo from "../images/crea_logo.svg";
import AetnaLogo from "../images/aetna_logo.svg";
import MethodLogo from "../images/method_logo.svg";
import DatchLogo from "../images/datch_logo.svg";
import SpectrocloudLogo from "../images/spectrocloud_logo.svg";
import PointerLogo from "../images/pointer_logo.png";
import DockerLogo from "../images/docker_logo.svg";
import GCPLogo from "../images/gcp_logo.svg";
import GoLogo from "../images/go_logo.svg";
import JavaLogo from "../images/java_logo.svg";
import KubernetesLogo from "../images/kubenetes_logo.svg";
import NickProfilePicture from "../images/NickProfilePicture.jpg";
import NodejsLogo from "../images/nodejs_logo.svg";
import PhpLogo from "../images/php_logo.svg";
import RubyLogo from "../images/ruby_logo.svg";
import RustLogo from "../images/rust_logo.svg";
import PythonLogo from "../images/python_logo.svg";
import XLogo from "../images/x_logo.svg";
import { LogoIcon } from "Logo";

function Orbit(props: { children: React.ReactElement[]; radius: number; numberOfItems: number }): React.ReactElement | null {
  const size = props.children.length ?? 1;
  const { numberOfItems } = props;

  return (
    <div className="pointer-events-none absolute top-0 left-0 w-full h-full">
      <div
        className={StyleUtils.mergeClassNames(
          "pointer-events-auto relative rounded-full border border-dashed border-zinc-700/40",
          props.radius > 300 ? "animate-spin-slow-2" : "animate-spin-slow-1",
          props.radius > 300 ? "rotate-90" : "",
        )}
        style={{
          top: `calc(120% - ${props.radius}px)`,
          left: `calc(50% - ${props.radius}px)`,
          width: `${2 * props.radius}px`,
          height: `${2 * props.radius}px`,
        }}
      >
        {ArrayUtils.extendByRepetition(props.children, numberOfItems).map((_element, i) => (
          <div
            key={i}
            className="absolute"
            style={{
              transform: `rotate(${90 - (i * 360) / numberOfItems}deg)`,
              left: `calc(50% - 16px + ${props.radius * Math.cos((i * 2 * Math.PI) / numberOfItems)}px)`,
              top: `calc(50% - 16px - ${props.radius * Math.sin((i * 2 * Math.PI) / numberOfItems)}px)`,
            }}
          >
            {props.children[i % size]}
          </div>
        ))}
      </div>
    </div>
  );
}

function IntegrationsOld(): React.ReactElement {
  function Icon(props: { name: string; src: string; href?: string }): React.ReactElement {
    const ret = (
      <div className="w-7 h-7 group relative">
        <div className="absolute w-full h-full flex justify-center items-center">
          <img
            className="absolute w-full transition ease-in-out opacity-[0.75] group-hover:opacity-[1.00] grayscale group-hover:grayscale-0 contrast-[0.25] group-hover:contrast-[1.00] brightness-[1.00] group-hover:brightness-[1.00]"
            src={props.src}
            alt={props.name}
          />
        </div>
        <span className="whitespace-pre hidden group-hover:block select-none absolute left-[50%] translate-x-[-50%] -top-7 text-center text-[10px] font-medium leading-[10px] bg-zinc-800 text-zinc-400 px-2 py-1 rounded-sm border border-zinc-700">
          {props.name}
        </span>
      </div>
    );

    if (props.href) {
      return (
        <a target="_blank" href={props.href}>
          {ret}
        </a>
      );
    }
    return ret;
  }

  return (
    <div className="overflow-hidden absolute left-0 top-0 w-full h-full">
      <div className="relative top-0 left-0 w-full h-full">
        <div
          className="absolute top-[calc(100%-8rem)] left-[calc(50%-12rem)] w-[24rem] h-[16rem] blur-[64px]"
          style={{
            background: "radial-gradient(#fcd34d40 0%, #fcd34d20 25%, transparent 50%, transparent 100%)",
          }}
        />
        <Orbit radius={360} numberOfItems={16}>
          <Icon name="Node.js" src={NodejsLogo} href="/docs/nodejs-express" />
          <Icon name="Python" src={PythonLogo} href="/docs/python-fastapi" />
          <Icon name="Go" src={GoLogo} href="/docs/golang" />
          <Icon name="Java" src={JavaLogo} />
          <Icon name="PHP" src={PhpLogo} href="/docs/php-laravel" />
          <Icon name="Rust" src={RustLogo} />
          <Icon name="Ruby" src={RubyLogo} />
        </Orbit>
        <Orbit radius={240} numberOfItems={12}>
          <Icon name="Docker" src={DockerLogo} href="/docs/docker" />
          <Icon name="Kubernetes" src={KubernetesLogo} href="/docs/kubernetes" />
          <Icon name="AWS" src={AWSLogo} />
          <Icon name="Azure" src={AzureLogo} />
          <Icon name="Google Cloud" src={GCPLogo} />
        </Orbit>
      </div>
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function DemoVideos() {
  const videoId = React.useId();
  const [selected, setSelected] = React.useState("Search");
  const [isPlaying, setPlaying] = React.useState(false);

  let url: string;
  switch (selected) {
    case "Search":
      url = "https://subtrace-static.pages.dev/search.mp4";
      break;
    case "Inspect":
      url = "https://subtrace-static.pages.dev/inspect.mp4";
      break;
    case "Replay":
      url = "https://subtrace-static.pages.dev/replay.mp4";
      break;
    case "Collaborate":
      url = "https://subtrace-static.pages.dev/collaborate.mp4";
      break;
    default:
      throw new Error(`unexpected selected: ${selected}`);
  }

  const select = (text: string) => {
    setSelected(text);
    setPlaying(true);
    TelemetryUtils.log({ type: "select_video", video: text });
  };

  const Button = (id: number, text: string) => {
    return (
      <button
        onClick={() => select(text)}
        className={[
          "text-xs font-medium px-2 md:px-4 py-2 flex items-center space-x-2",
          text === selected ? "text-zinc-300 hover:text-zinc-300" : "text-zinc-400/80 hover:text-zinc-300/80",
        ].join(" ")}
      >
        <span
          className={["text-[10px] bg-zinc-800/40 rounded-full w-6 h-6 hidden md:flex items-center justify-center", text === selected ? "text-zinc-300" : "text-zinc-500/80"].join(
            " ",
          )}
        >
          {text === selected ? <StepForwardIcon size={10} /> : <span>{id}</span>}
        </span>
        <span>{text}</span>
      </button>
    );
  };

  React.useEffect(() => {
    const el = document.getElementById(videoId) as HTMLVideoElement;
    if (isPlaying) {
      try {
        el.play();
      } catch {}
    } else {
      try {
        el.pause();
      } catch {}
    }
  });

  const toggle = () => {
    const el = document.getElementById(videoId) as HTMLVideoElement;
    if (el.paused) {
      setPlaying(true);
      TelemetryUtils.log({ type: "play_video", video: selected });
    } else {
      setPlaying(false);
      TelemetryUtils.log({ type: "pause_video", video: selected });
    }
  };

  return (
    <div className="w-full flex flex-col space-y-2 items-center justify-center">
      <div className="rounded-xl w-full bg-zinc-800/20 p-2">
        <div className="rounded-lg w-full bg-zinc-800/20 flex justify-center items-center space-x-2 md:space-x-4">
          {Button(1, "Search")}
          {Button(2, "Inspect")}
          {Button(3, "Replay")}
          {Button(4, "Collaborate")}
        </div>
      </div>
      <div className="rounded-xl w-full bg-zinc-800/30 p-2 overflow-hidden relative group flex">
        <div className="aspect-[1920/1080] rounded-lg flex justify-center items-center w-full h-full overflow-hidden">
          <video loop id={videoId} src={url} controls={false} width="100%" height="100%" className="w-full h-full">
            <span className="text-zinc-200 mx-auto my-auto">{selected}</span>
          </video>
        </div>
        <button
          onClick={() => toggle()}
          className={[
            "absolute top-0 left-0 w-full h-full flex justify-center items-center text-xs font-medium transition-all duration-[100ms]",
            isPlaying ? "bg-transparent" : "bg-black/30",
          ].join(" ")}
        >
          {isPlaying ? (
            <div className="w-32 h-32 hidden md:group-hover:flex flex-col space-y-4 justify-center items-center rounded-xl bg-zinc-900/70 backdrop-brightness-125 backdrop-blur-xl text-zinc-100 p-4">
              <PauseIcon size={32} />
              <span>Pause</span>
            </div>
          ) : (
            <div className="w-32 h-32 brightness-[0.90] group-hover:brightness-[1.00] space-y-4 flex flex-col justify-center items-center rounded-xl bg-zinc-900/70 backdrop-brightness-125 backdrop-blur-xl text-zinc-100 p-4">
              <PlayIcon size={32} />
              <span>Show Example</span>
            </div>
          )}
        </button>
      </div>
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function CodeSnippet() {
  const code: Record<string, string> = {
    Python: "python main.py",
    Node: "node server.js",
    Go: "go run .",
    FastAPI: "fastapi run main.py",
    Flask: "flask --app hello run",
    Gunicorn: "gunicorn -w 4 myapp:app",
    Laravel: "php artisan serve",
  };

  const [selectedLanguage, setSelectedLanguage] = React.useState("Python");

  return (
    <div className="w-full bg-zinc-900 px-6 pt-[10px] pb-[20px] flex flex-col space-y-[6px]">
      <div className="flex mx-[-6px] items-center">
        {Object.entries(code).map(([language]) => (
          <button
            key={language}
            onClick={() => setSelectedLanguage(language)}
            className={[
              "cursor-pointer font-medium px-[6px] py-[2px] text-[10px]",
              language === selectedLanguage ? "text-zinc-400/80 hover:text-zinc-400" : "text-zinc-600/70 hover:text-zinc-600",
            ].join(" ")}
          >
            {language}
          </button>
        ))}
      </div>
      <div className="md:min-w-[320px] flex space-x-[10px] text-[12px] items-center">
        <span className="text-zinc-500/70 select-none">$</span>
        <span className="font-mono font-medium text-zinc-300/80">subtrace run -- {code[selectedLanguage]}</span>
      </div>
    </div>
  );
}

function getCommand(lang: string) {
  const command: Record<string, string> = {
    Python: "python main.py",
    Node: "node server.js",
    Go: "./main",
    Rust: "./main",
    PHP: "php artisan serve",
    Ruby: "ruby app.rb",
  };
  return command[lang];
}

function Supports(props: { logos: { name: string; icon: any }[]; selectedLanguage: string | undefined; setSelectedLanguage: (_: string | undefined) => void }) {
  const [label, setLabel] = React.useState({ id: 0, text: "", transition: false });
  const [forceCode, setForceCode] = React.useState(false);

  const icons: React.ReactElement[] = [];
  for (let i = 0; i < props.logos.length; i++) {
    const { name, icon } = props.logos[i];

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const iconId = React.useId();

    // eslint-disable-next-line react-hooks/rules-of-hooks
    React.useEffect(() => {
      const el = document.getElementById(iconId)!;
      const fn = () => {
        setLabel(({ id }) => {
          setTimeout(() => {
            setLabel((prev) => {
              if (prev.id === id + 1) {
                return { id: prev.id + 1, text: prev.text, transition: false };
              }
              return prev;
            });
          }, 150);
          return { id: id + 1, text: name, transition: true };
        });
      };
      el.addEventListener("mouseenter", fn);
      return () => el.removeEventListener("mouseenter", fn);
    }, [iconId, name]);

    const onClick = () => {
      props.setSelectedLanguage(name);
      setForceCode(true);

      const el = document.getElementById(iconId)!;
      let fn = () => {};
      fn = () => {
        el.removeEventListener("mouseleave", fn);
        setForceCode(false);
      };
      el.addEventListener("mouseleave", fn);
    };

    icons.push(
      <button
        id={iconId}
        key={name}
        onClick={() => onClick()}
        className="w-[56px] h-[56px] flex justify-center items-center group/icon relative outline outline-[1px] outline-offset-[-1px] outline-transparent hover:outline-zinc-700 overflow-hidden bg-transparent hover:bg-zinc-400/10 rounded-lg relative active:scale-[0.95]"
      >
        {(() => {
          if (props.selectedLanguage !== name) {
            return;
          }
          return (
            <div className="absolute top-[6px] right-[6px] w-[10px] h-[10px] rounded-full bg-zinc-400 group-hover/list:brightness-[0.35] group-hover/icon:!brightness-[1.20] text-zinc-800 flex justify-center items-center">
              <CheckIcon size={7} strokeWidth={5} />
            </div>
          );
        })()}
        <div className="z-[0] w-[28px] h-[28px] flex justify-center items-center group-hover/list:opacity-[0.35] group-hover/icon:!opacity-[1] brightness-[0.80] group-hover/icon:brightness-[1.00]">
          <img src={icon} style={{ filter: "grayscale(1) opacity(0.75) brightness(200%) contrast(35%)" }} />
        </div>
      </button>,
    );
  }

  const iconsId = React.useId();
  React.useEffect(() => {
    if (props.selectedLanguage) {
      return;
    }
    const el = document.getElementById(iconsId)!;
    const fn = () => setLabel(({ id }) => ({ id: id + 1, text: "", transition: false }));
    el.addEventListener("mouseleave", fn);
    return () => el.removeEventListener("mouseleave", fn);
  }, [iconsId, props.selectedLanguage]);

  return (
    <div className="w-full mx-auto max-w-[720px] flex items-center justify-end relative group/container">
      <div className="w-full grow" />
      <div id={iconsId} className="justify-end grow-0 max-w-fit flex group/list peer/list select-none">
        {icons}
      </div>
      <div
        className={[
          "z-[1] absolute top-0 left-0 flex justify-begin items-center w-fit h-full max-h-[72px] transition-all duration-[200ms] ease-in-out pointer-events-none select-none",
          forceCode
            ? "opacity-[0] blur-[12px]"
            : props.selectedLanguage === undefined
              ? "opacity-[1] blur-[0px]"
              : "opacity-[0] blur-[12px] peer-hover/list:opacity-[1] peer-hover/list:blur-[0px]",
        ].join(" ")}
      >
        <div className="flex flex-col text-[17px] font-medium">
          <span className="text-zinc-500">Use Subtrace with</span>
          <span style={{ animation: label.transition ? "125ms ease-in 0ms 1 normal both running slide-text-enter" : "" }} className="transition-all text-zinc-300">
            {label.text === "" ? "any language" : label.text}
          </span>
        </div>
      </div>
      <div
        style={{ maxWidth: `min(400px, calc(100% - 16px - ${props.logos.length * 56}px))` }}
        className={[
          "z-[2] absolute top-0 left-0 w-full h-full max-h-[72px] flex items-center transition-all duration-[200ms] ease-in-out",
          forceCode
            ? "opacity-[1] blur-[0px]"
            : props.selectedLanguage === undefined
              ? "opacity-[0] blur-[12px]"
              : "opacity-[1] blur-[0px] peer-hover/list:opacity-[0] peer-hover/list:blur-[12px]",
          props.selectedLanguage ? "" : "pointer-events-none select-none",
        ].join(" ")}
      >
        <div className="w-full max-w-full overflow-x-scroll overflow-hide-scrollbar rounded bg-zinc-900 font-mono whitespace-pre text-[10px] leading-[20px] px-4 py-2 relative">
          <div className="hidden lg:block select-none pointer-events-none absolute bottom-[2px] right-[6px] text-[8px] text-zinc-200/10 font-bold uppercase font-sans">
            {props.selectedLanguage}
          </div>
          <span className="text-zinc-700/50 font-semibold select-none">{"$ "}</span>
          <span className="text-zinc-400">curl https://subtrace.dev/install.sh | sh -</span>
          <br />
          <span className="text-zinc-700/50 font-semibold select-none">{"$ "}</span>
          <span className="text-zinc-400">subtrace run -- {props.selectedLanguage !== undefined ? getCommand(props.selectedLanguage) : ""}</span>
        </div>
      </div>
    </div>
  );
}

const GameOfLife = () => {
  const CELL_SIZE = 24;
  const TICK_MS = 1000;
  const MAX_HIST = 8;

  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const hoverRef = React.useRef<HTMLCanvasElement | null>(null);
  const lastHover = React.useRef<{ r: number; c: number } | null>(null);

  const bufferRef = React.useRef<Uint8Array>(new Uint8Array(0));

  const [grid, setGrid] = React.useState<Uint8Array>(new Uint8Array(0));

  const [mole, setMole] = React.useState<{ r: number; c: number } | null>(null);

  const [hist, setHist] = React.useState<{ r: number; c: number }[]>([]);

  const [{ cols, rows }, setGeom] = React.useState({ cols: 0, rows: 0 });

  const idx = React.useCallback((r: number, c: number): number => ((r + rows) % rows) * cols + ((c + cols) % cols), [rows, cols]);

  React.useEffect(() => {
    function handleMouseUp() {
      painting.current = false;
    }

    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  React.useEffect(() => {
    if (!cols || !rows) return;
    const b = bufferRef.current;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const i = r * cols + c;
        b[i] = Math.random() < 0.5 ? 1 : 0;
      }
    }

    let p = new Uint8Array(b);
    for (let i = 0; i < 120; i++) {
      let total = 0;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const i = r * cols + c;
          let n = 0;
          n += p[idx(r - 1, c - 1)];
          n += p[idx(r - 1, c)];
          n += p[idx(r - 1, c + 1)];
          n += p[idx(r, c - 1)];
          n += p[idx(r, c + 1)];
          n += p[idx(r + 1, c - 1)];
          n += p[idx(r + 1, c)];
          n += p[idx(r + 1, c + 1)];
          const alive = p[i] === 1;
          if (alive) {
            total++;
          }
          b[i] = n === 3 || (alive && n === 2) ? 1 : 0;
        }
      }
      p = new Uint8Array(b);
      if (total < 0.25 * rows * cols) {
        break;
      }
    }

    setHist([]);
    setGrid(new Uint8Array(b));
  }, [cols, rows, idx]);

  React.useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const newCols = Math.floor(rect.width / CELL_SIZE) || 0;
      const newRows = Math.floor(rect.height / CELL_SIZE) || 0;
      if (newCols === cols && newRows === rows) return;

      const size = newCols * newRows;
      setGeom({ cols: newCols, rows: newRows });
      setGrid(new Uint8Array(size));
      bufferRef.current = new Uint8Array(size);
      if (hoverRef.current) {
        hoverRef.current.width = newCols * CELL_SIZE;
        hoverRef.current.height = newRows * CELL_SIZE;
      }
    };

    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, [cols, rows]);

  const locate = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.floor((e.clientX - rect.left) / CELL_SIZE);
    const y = Math.floor((e.clientY - rect.top) / CELL_SIZE);
    return { r: y, c: x, i: idx(y, x) };
  };

  const drawHover = (r: number, c: number) => {
    const hov = hoverRef.current;
    if (!hov) return;
    const ctx = hov.getContext("2d")!;
    if (lastHover.current) {
      const { r: lr, c: lc } = lastHover.current;
      if (lr === r && lc === c) {
        return;
      }
      ctx.clearRect(lc * CELL_SIZE, lr * CELL_SIZE, CELL_SIZE, CELL_SIZE);
    }
    ctx.fillStyle = "#52525b20";
    ctx.fillRect(c * CELL_SIZE, r * CELL_SIZE, CELL_SIZE, CELL_SIZE);
    ctx.strokeStyle = "#52525b40";
    ctx.lineWidth = 1;
    ctx.strokeRect(c * CELL_SIZE + 0.5, r * CELL_SIZE + 0.5, CELL_SIZE - 1, CELL_SIZE - 1);
    lastHover.current = { r, c };
  };

  const handleMouseLeave = () => {
    const hov = hoverRef.current;
    if (hov) hov.getContext("2d")!.clearRect(0, 0, hov.width, hov.height);
    lastHover.current = null;
  };

  const painting = React.useRef(false);

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    painting.current = true;
    const { i } = locate(e);
    setGrid((g) => {
      const n = g.slice();
      n[i] = 1;
      return n;
    });
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const { r, c, i } = locate(e);
    if (e.pointerType === "mouse") {
      drawHover(r, c);
    }
    if (painting.current) {
      setGrid((g) => {
        if (g[i]) return g;
        const n = g.slice();
        n[i] = 1;
        return n;
      });
    }
  };

  const step = React.useCallback(() => {
    if (!cols || !rows) return -1;
    let ret = 0;
    const g = grid;
    const b = bufferRef.current;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const i = r * cols + c;
        let n = 0;
        n += g[idx(r - 1, c - 1)];
        n += g[idx(r - 1, c)];
        n += g[idx(r - 1, c + 1)];
        n += g[idx(r, c - 1)];
        n += g[idx(r, c + 1)];
        n += g[idx(r + 1, c - 1)];
        n += g[idx(r + 1, c)];
        n += g[idx(r + 1, c + 1)];
        ret += g[i];
        const alive = g[i] === 1;
        b[i] = n === 3 || (alive && n === 2) ? 1 : 0;
      }
    }

    let nextMole = mole;
    const aliveAt = (r: number, c: number) => !!b[idx(r, c)];
    const randomOf = <T,>(arr: T[]) => arr[Math.floor(Math.random() * arr.length)];

    if (mole && Math.random() < 0.9) {
      const { r: mr, c: mc } = mole;
      if (!aliveAt(mr, mc) || Math.random() < 0.5) {
        const neigh: { r: number; c: number }[] = [];
        for (let dr = -1; dr <= 1; dr++)
          for (let dc = -1; dc <= 1; dc++) {
            if (!dr && !dc) continue;
            const nr = mr + dr,
              nc = mc + dc;
            if (aliveAt(nr, nc)) neigh.push({ r: nr, c: nc });
          }
        nextMole =
          neigh.length > 0
            ? randomOf(neigh)
            : (() => {
                const alive: number[] = [];
                for (let i = 0; i < b.length; i++) if (b[i]) alive.push(i);
                if (!alive.length) return null;
                const pick = randomOf(alive);
                return { r: Math.floor(pick / cols), c: pick % cols };
              })();
      }
    } else {
      const alive: number[] = [];
      for (let i = 0; i < b.length; i++) if (b[i]) alive.push(i);
      if (alive.length) {
        const pick = randomOf(alive);
        nextMole = { r: Math.floor(pick / cols), c: pick % cols };
      }
    }

    setHist((prev) => {
      if (!nextMole) {
        return prev;
      }
      let next = [...prev];
      if (next.length === 0 || next[next.length - 1].c !== nextMole.c || next[next.length - 1].r !== nextMole.r) {
        next.push(nextMole);
      }
      if (next.length > MAX_HIST) {
        next = next.slice(next.length - MAX_HIST);
      }
      return next;
    });
    setMole(nextMole);
    setGrid(new Uint8Array(b));
    return ret;
  }, [grid, mole, idx, cols, rows]);

  const draw = React.useCallback((): void => {
    const canvas = canvasRef.current;
    if (!canvas || !cols || !rows) return;
    const width = cols * CELL_SIZE;
    const height = rows * CELL_SIZE;
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d", { alpha: false });
    if (!ctx) return;

    ctx.fillStyle = "#09090b";
    ctx.fillRect(0, 0, width, height);

    for (let i = 0; i < grid.length; i++) {
      if (!grid[i]) continue;
      const r = Math.floor(i / cols);
      const c = i % cols;
      let n = 0;
      n += grid[idx(r - 1, c - 1)];
      n += grid[idx(r - 1, c)];
      n += grid[idx(r - 1, c + 1)];
      n += grid[idx(r, c - 1)];
      n += grid[idx(r, c + 1)];
      n += grid[idx(r + 1, c - 1)];
      n += grid[idx(r + 1, c)];
      n += grid[idx(r + 1, c + 1)];
      ctx.fillStyle = "#27272a" + Math.round((0.2 + 0.8 * Math.pow(n / 8, 2)) * 255).toString(16);
      ctx.fillRect(c * CELL_SIZE, r * CELL_SIZE, CELL_SIZE, CELL_SIZE);
    }

    if (hist.length >= 2) {
      ctx.beginPath();
      ctx.strokeStyle = "#36363a80"; // + Math.round((0.30 + (1 - Math.pow(hist.length / MAX_HIST, 0.75)) * 0.20) * 255).toString(16);
      ctx.lineWidth = 1.5;
      ctx.moveTo(hist[0].c * CELL_SIZE + CELL_SIZE / 2, hist[0].r * CELL_SIZE + CELL_SIZE / 2);
      for (let i = 1; i < hist.length; i++) {
        ctx.lineTo(hist[i].c * CELL_SIZE + CELL_SIZE / 2, hist[i].r * CELL_SIZE + CELL_SIZE / 2);
      }
      ctx.stroke();
      ctx.fillStyle = "#404046";
      for (let i = 0; i < hist.length; i++) {
        ctx.fillRect(hist[i].c * CELL_SIZE + CELL_SIZE / 2 - 0.5, hist[i].r * CELL_SIZE + CELL_SIZE / 2 - 0.5, 1, 1);
      }
    }

    if (mole) {
      ctx.fillStyle = "#3f3f46";
      ctx.fillRect(mole.c * CELL_SIZE + 5, mole.r * CELL_SIZE + 5, CELL_SIZE - 10, CELL_SIZE - 10);
      ctx.fillStyle = "#f4f4f5";
      ctx.fillRect(mole.c * CELL_SIZE + CELL_SIZE / 2 - 1, mole.r * CELL_SIZE + CELL_SIZE / 2 - 3, 2, 6);
      ctx.fillRect(mole.c * CELL_SIZE + CELL_SIZE / 2 - 3, mole.r * CELL_SIZE + CELL_SIZE / 2 - 1, 6, 2);
    }
  }, [grid, mole, hist, cols, rows, idx]);

  const [stats, setStats] = React.useState({ time: 0, alive: 0 });

  React.useEffect(() => {
    const id = window.setInterval(() => {
      if (painting.current) {
        return;
      }
      const alive = step();
      setStats((prev) => ({ time: prev.time + 1, alive }));
    }, TICK_MS);
    return () => clearInterval(id);
  }, [step, painting]);

  React.useEffect(draw, [draw]);

  return (
    <div className="cursor-crosshair w-full max-w-[1200px] h-[calc(100vh-48px-96px-16px)] -aspect-[1] -md:aspect-[4/3] -lg:aspect-[8/5] -max-h-[calc(100vh-12rem)] overflow-hidden flex relative">
      <canvas
        ref={canvasRef}
        onPointerMove={handlePointerMove}
        onMouseLeave={handleMouseLeave}
        onMouseDown={handleMouseDown}
        className="ph-no-capture z-[0] absolute top-0 left-0 w-full h-full select-none"
        style={{ imageRendering: "pixelated" }}
      />
      <canvas ref={hoverRef} className="ph-no-capture z-[1] absolute top-0 left-0 w-full h-full pointer-events-none" style={{ imageRendering: "pixelated" }} />
      <div className="z-[2] pointer-events-none absolute top-0 left-0 w-full h-full border-b xl:border-x border-zinc-900" />
      <div className="z-[2] hidden pointer-events-none select-none absolute bottom-[24px] md:bottom-[48px] right-[36px] md:right-[72px] leading-[1.33] text-[8px] font-bold text-zinc-600/50 font-mono whitespace-pre">
        {`TIME ${stats.time.toString().padStart(5)}`}
        <br />
        {`LIVE ${stats.alive.toString().padStart(5)}`}
        <br />
        {`DEAD ${(rows * cols - stats.alive).toString().padStart(5)}`}
      </div>
      <div className="z-[3] pointer-events-none select-none absolute top-[24px] left-[36px] md:left-[72px] h-[32px] flex justify-center items-center space-x-1">
        <LogoIcon className="text-zinc-100 w-[18px] h-[18px]" />
        <span className="text-zinc-100 font-medium text-[12px] font-mono">subtrace</span>
      </div>
      <div className="z-[3] absolute top-[72px] left-[36px] md:left-[72px] text-3xl md:text-4xl lg:text-5xl font-semibold text-zinc-100 !leading-[1.30]">
        <div className="select-none pointer-events-none">
          Zero Code Network
          <br />
          Tracer
        </div>
      </div>
      <div className="z-[3] absolute top-[24px] right-[36px] md:right-[72px] flex items-center space-x-2">
        <a
          target="_blank"
          href="https://github.com/subtrace/subtrace"
          className="h-[32px] flex justify-center items-center space-x-2 rounded bg-transparent transition-bg duration-[150ms] ease-in-out text-zinc-300 hover:text-zinc-100 hover:bg-zinc-900 px-[10px]"
        >
          <svg className="w-[16px] h-[16px]" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" viewBox="0 0 24 24" display="inline-block" overflow="visible">
            <path
              fill="currentColor"
              d="M12.5.75C6.146.75 1 5.896 1 12.25c0 5.089 3.292 9.387 7.863 10.91.575.101.79-.244.79-.546 0-.273-.014-1.178-.014-2.142-2.889.532-3.636-.704-3.866-1.35-.13-.331-.69-1.352-1.18-1.625-.402-.216-.977-.748-.014-.762.906-.014 1.553.834 1.769 1.179 1.035 1.74 2.688 1.25 3.349.948.1-.747.402-1.25.733-1.538-2.559-.287-5.232-1.279-5.232-5.678 0-1.25.445-2.285 1.178-3.09-.115-.288-.517-1.467.115-3.048 0 0 .963-.302 3.163 1.179.92-.259 1.897-.388 2.875-.388.977 0 1.955.13 2.875.388 2.2-1.495 3.162-1.179 3.162-1.179.633 1.581.23 2.76.115 3.048.733.805 1.179 1.825 1.179 3.09 0 4.413-2.688 5.39-5.247 5.678.417.36.776 1.05.776 2.128 0 1.538-.014 2.774-.014 3.162 0 .302.216.662.79.547C20.709 21.637 24 17.324 24 12.25 24 5.896 18.854.75 12.5.75Z"
            ></path>
          </svg>
          <div className="leading-[12px] text-[11px] pt-[1px] font-medium">2.7K</div>
        </a>
        <a
          href="/login"
          className="max-w-fit whitespace-pre group/button px-4 h-[32px] flex justify-center items-center space-x-2 rounded w-full text-xs font-medium bg-zinc-100 hover:bg-zinc-50 active:scale-[0.95] cursor-pointer text-zinc-700"
        >
          <div>Start</div>
          <div className="duration-[100ms] translate-x-[-1px] group-hover/button:translate-x-[+1px]">&rarr;</div>
        </a>
      </div>
      <div className="z-[4] pointer-events-none absolute bottom-[64px] md:bottom-[84px] left-[36px] md:left-[72px] flex flex-col">
        <div className="h-[24px] select-none font-mono text-zinc-200 text-[10px] uppercase font-medium">One command setup</div>
        <div className="h-[24px] select-none font-mono text-zinc-200 text-[10px] uppercase font-medium">All major languages</div>
        <div className="h-[24px] select-none font-mono text-zinc-200 text-[10px] uppercase font-medium">Realtime prod debugging</div>
      </div>
      <div className="z-[4] absolute bottom-[24px] md:bottom-[48px] left-[36px] md:left-[72px] flex flex-col space-y-1">
        <div className="flex items-center space-x-1">
          <div className="w-[210px] h-[32px] rounded bg-zinc-100 cursor-pointer hover:bg-zinc-50 font-medium flex flex-col justify-center items-center group transition-all duration-[100ms] linear active:scale-[0.95]">
            <a href="/login" className="w-full text-zinc-700 flex justify-center items-center space-x-1">
              <span className="text-[13px] font-medium select-none">Try for free</span>
              <ArrowRight strokeWidth={2.75} size={13} className="transition-all duration-[150ms] ease-in-out translate-x-[-1px] group-hover:translate-x-[+1px]" />
            </a>
          </div>
          <div />
          <div className="w-[32px] h-[32px] text-zinc-300 hover:text-zinc-100 flex justify-center items-center group/yc relative">
            <svg className="w-[16px] h-[16px]" viewBox="0 0 256 256" version="1.1" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid">
              <g>
                <rect fill="currentColor" x="0" y="0" width="256" height="256"></rect>
                <path
                  d="M119.373653,144.745813 L75.43296,62.4315733 L95.5144533,62.4315733 L121.36192,114.52416 C121.759575,115.452022 122.2235,116.413008 122.753707,117.407147 C123.283914,118.401285 123.747838,119.428546 124.145493,120.48896 C124.410597,120.886615 124.609422,121.251127 124.741973,121.582507 C124.874525,121.913886 125.007075,122.212123 125.139627,122.477227 C125.802386,123.802744 126.39886,125.095105 126.929067,126.354347 C127.459274,127.613589 127.923198,128.773399 128.320853,129.833813 C129.381268,127.580433 130.541078,125.1614 131.80032,122.57664 C133.059562,119.99188 134.351922,117.307747 135.67744,114.52416 L161.92256,62.4315733 L180.612267,62.4315733 L136.27392,145.739947 L136.27392,198.826667 L119.373653,198.826667 L119.373653,144.745813 Z"
                  fill="#000000"
                ></path>
              </g>
            </svg>
            <div className="absolute top-[-16px] left-[8px] hidden group-hover/yc:flex justify-center items-center text-[10px] whitespace-pre font-medium">
              Backed by Y Combinator
            </div>
          </div>
          <a
            target="_blank"
            href="https://github.com/subtrace/subtrace"
            className="h-[32px] flex justify-center items-center space-x-2 rounded bg-transparent transition-bg duration-[150ms] ease-in-out text-zinc-300 hover:text-zinc-100"
          >
            <svg
              className="w-[16px] h-[16px]"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
              focusable="false"
              viewBox="0 0 24 24"
              display="inline-block"
              overflow="visible"
            >
              <path
                fill="currentColor"
                d="M12.5.75C6.146.75 1 5.896 1 12.25c0 5.089 3.292 9.387 7.863 10.91.575.101.79-.244.79-.546 0-.273-.014-1.178-.014-2.142-2.889.532-3.636-.704-3.866-1.35-.13-.331-.69-1.352-1.18-1.625-.402-.216-.977-.748-.014-.762.906-.014 1.553.834 1.769 1.179 1.035 1.74 2.688 1.25 3.349.948.1-.747.402-1.25.733-1.538-2.559-.287-5.232-1.279-5.232-5.678 0-1.25.445-2.285 1.178-3.09-.115-.288-.517-1.467.115-3.048 0 0 .963-.302 3.163 1.179.92-.259 1.897-.388 2.875-.388.977 0 1.955.13 2.875.388 2.2-1.495 3.162-1.179 3.162-1.179.633 1.581.23 2.76.115 3.048.733.805 1.179 1.825 1.179 3.09 0 4.413-2.688 5.39-5.247 5.678.417.36.776 1.05.776 2.128 0 1.538-.014 2.774-.014 3.162 0 .302.216.662.79.547C20.709 21.637 24 17.324 24 12.25 24 5.896 18.854.75 12.5.75Z"
              ></path>
            </svg>
            <div className="leading-[12px] text-[11px] pt-[1px] font-medium">2.7K</div>
          </a>
        </div>
      </div>
    </div>
  );
};

function GraphicOmniscience(props: { selectedLanguage: string | undefined }) {
  const [state, setState] = React.useState<"init" | "terminal" | "transitionTerminalBrowser" | "browser">("init");

  const [terminalText, setTerminalText] = React.useState("");

  const [requests, setRequests] = React.useState<string[]>([]);

  const [browserFade, setBrowserFade] = React.useState(false);

  switch (state) {
    case "init":
      // eslint-disable-next-line react-hooks/rules-of-hooks
      React.useEffect(() => {
        setTerminalText("");
        setRequests([]);
        setBrowserFade(false);
        setTimeout(() => setState((prev) => (prev === "init" ? "terminal" : prev)), 100);
      }, [state]); // eslint-disable-line react-hooks/exhaustive-deps
      return (
        <React.Fragment>
          <div
            key="terminal"
            className="select-none leading-[12px] sm:leading-[16px] absolute top-0 left-0 w-full h-full whitespace-pre font-mono bg-zinc-900/30 border border-zinc-900 rounded-lg p-4 sm:p-6 opacity-[0]"
          >
            <span className="font-medium text-zinc-700/80">{"user@prod:~$ "}</span>
            <div className="inline-block w-[5px] sm:w-[6px] h-[9px] sm:h-[12px] bg-red-500"></div>
          </div>
        </React.Fragment>
      );

    case "terminal":
      // eslint-disable-next-line react-hooks/rules-of-hooks
      React.useEffect(() => {
        let interval: number;
        // eslint-disable-next-line prefer-const
        interval = setInterval(() => {
          setTerminalText((prev) => {
            // Using \u200b (zero width space) as a hack to simulate typing pauses lol.
            const want = [
              "\u200b".repeat(10),
              "subtrace ",
              "\u200b".repeat(4),
              "run ",
              "\u200b".repeat(7),
              "-- ",
              "\u200b".repeat(3),
              getCommand(props.selectedLanguage ?? "Python"),
              "\u200b".repeat(7),
              "\n",
              "\u200b".repeat(8),
              ".",
              "\u200b".repeat(8),
              ".",
              "\u200b".repeat(8),
              ".",
              "\u200b".repeat(8),
              " ",
              "\u2713",
              "\n",
            ].join("");

            if (prev.length === want.length) {
              clearInterval(interval);
              setTimeout(() => setState((prev) => (prev === "terminal" ? "transitionTerminalBrowser" : prev)), 500);
              return prev;
            }
            return want.substring(0, prev.length + 1);
          });
        }, 50);
      }, [state]); // eslint-disable-line react-hooks/exhaustive-deps
      return (
        <React.Fragment>
          <div
            key="terminal"
            className="select-none leading-[12px] sm:leading-[16px] absolute top-0 left-0 w-full h-full whitespace-pre font-mono bg-zinc-900/30 border border-zinc-900 rounded-lg p-4 sm:p-6 opacity-[1] transition-opacity duration-[300ms] ease-in-out"
          >
            <span className="font-medium text-zinc-700/80">{"user@prod:~$ "}</span>
            {terminalText}
            <div className="inline-block w-[5px] sm:w-[6px] h-[9px] sm:h-[12px] bg-red-500"></div>
          </div>
        </React.Fragment>
      );

    case "transitionTerminalBrowser":
      // eslint-disable-next-line react-hooks/rules-of-hooks
      React.useEffect(() => {
        setTimeout(() => setState((prev) => (prev === "transitionTerminalBrowser" ? "browser" : prev)), 450);
      }, [state]); // eslint-disable-line react-hooks/exhaustive-deps
      return (
        <React.Fragment>
          <div
            key="terminal"
            className="select-none leading-[12px] sm:leading-[16px] absolute top-0 left-0 w-[100%] h-full whitespace-pre font-mono bg-zinc-900/30 border border-zinc-900 rounded-lg p-4 sm:p-6 translate-x-[-200%] transition-transform ease-in-out duration-[350ms]"
          >
            <span className="font-medium text-zinc-700/80">{"user@prod:~$ "}</span>
            {terminalText}
            <div className="inline-block w-[5px] sm:w-[6px] h-[9px] sm:h-[12px] bg-red-500"></div>
          </div>
          <div key="browser" className="scale-[0]" />
        </React.Fragment>
      );

    case "browser":
      // eslint-disable-next-line react-hooks/rules-of-hooks
      React.useEffect(() => {
        const lognorm = () => {
          let u = 0,
            v = 0,
            r = 0;
          while (r === 0 || r >= 1) {
            u = 2 * Math.random() - 1;
            v = 2 * Math.random() - 1;
            r = u * u + v * v;
          }
          const c = Math.sqrt((-2 * Math.log(r)) / r);
          const z = u * c;
          return Math.exp(z);
        };

        const pick = (choices: string[]) => choices[Math.floor(Math.random() * choices.length)];

        const want: any[] = [];
        for (let i = 0; i < 300; i++) {
          const path = pick([
            "/health",
            "/v1/models",
            "/v1/chat/completions",
            "/v1/chat/completions",
            "/v1/chat/completions",
            "/api/users/me",
            `/api/users/${Math.random().toString(16).substring(2, 8)}`,
            `/api/file/${window.crypto.randomUUID()}`,
          ]);
          let latency = `${Math.round(lognorm() * 100)}ms`;
          let status = pick(["200", "200", "200", "200", "200", "200", "200", "200", "200", "200", "200", "200", "200", "200", "403", "408", "429", "500"]);
          if (status === "408") {
            latency = `${Math.round(lognorm() * 500)}ms`;
          }
          let method = pick(["GET", "GET", "POST", "POST", "POST", "POST", "PUT", "DELETE"]);
          if (path === "/health") method = "GET";
          if (path === "/v1/chat/completions") {
            if (Math.random() < 0.9) {
              method = "POST";
            } else {
              method = "GET";
              status = "405";
            }
          }
          if (i >= 15 && Math.random() < 0.4) {
            want.push(`${latency.padStart(7)}   ${status.padStart(3)}   ${method.split("").reverse().join("").padStart(6).split("").reverse().join("")}  ${path}`);
          } else {
            want.push(null);
          }
        }

        let interval: number;
        // eslint-disable-next-line prefer-const
        interval = setInterval(() => {
          setRequests((prev) => {
            if (prev.length === Math.round(0.75 * want.length)) {
              setTimeout(() => setBrowserFade(true), 0);
              setTimeout(() => {
                clearInterval(interval);
                setState((prev) => (prev === "browser" ? "init" : prev));
              }, 2000);
            }
            if (prev.length === want.length) {
              clearInterval(interval);
              return prev;
            }
            return [...want.slice(0, prev.length + 1)];
          });
        }, 35);
      }, [state]); // eslint-disable-line react-hooks/exhaustive-deps
      return (
        <React.Fragment>
          <div
            key="browser"
            className={[
              "absolute top-0 left-0 w-full h-full border-t border-zinc-800/50 rounded-lg bg-zinc-900/30 flex flex-col divide-y divide-zinc-800/30",
              browserFade ? "transition-opacity duration-[2000ms] ease-in opacity-[0]" : "transition-transform duration-[200ms] ease-out scale-[1]",
            ].join(" ")}
          >
            <div className="w-full grow-0 shrink-0 p-[4px] sm:p-[6px] pl-3 pb-[0px] h-[30px] sm:h-[36px] flex items-center space-x-3">
              <div className="grow-0 shrink-0 flex items-center space-x-1 pl-2">
                <div className="w-[10px] h-[10px] rounded-full bg-zinc-800/40" />
                <div className="w-[10px] h-[10px] rounded-full bg-zinc-800/40" />
                <div className="w-[10px] h-[10px] rounded-full bg-zinc-800/40" />
              </div>
              <lucide.ArrowLeft size={14} strokeWidth={3.5} className="text-zinc-200 opacity-[0.15]" />
              <lucide.RotateCw size={14} strokeWidth={3.5} className="text-zinc-200 opacity-[0.15]" />
              <a
                href="/dashboard"
                className="grow h-[calc(100%-4px)] sm:h-full w-full rounded-md bg-zinc-800/40 flex items-center px-3 space-x-2 font-sans hover:brightness-[1.20]"
              >
                <lucide.Globe size={11} strokeWidth={3.5} className="text-zinc-200 opacity-[0.20]" />
                <span className="font-semibold text-zinc-200/15 leading-[10px] text-[10px]">subtrace.dev/dashboard</span>
              </a>
            </div>
            <div className="w-full grow-0 shrink-0 mt-[4px] sm:mt-[2px] flex h-[calc(100%-30px)] sm:h-[calc(100%-36px)] divide-x divide-zinc-800/30">
              <div className="h-full flex flex-col justify-between pl-2 pr-8 pt-3 pb-5 w-[64px] sm:w-[80px] bg-black/15">
                <div className="w-full flex flex-col space-y-[6px] sm:space-y-2">
                  <div className="w-full px-1 pb-2 rounded flex items-center">
                    <LogoIcon className="w-[11px] h-[11px] text-zinc-400" />
                  </div>
                  <div className="w-[32px] h-[10px] bg-zinc-800/30 rounded flex items-center" />
                  <div className="w-[40px] h-[10px] bg-zinc-800/30 rounded flex items-center" />
                  <div className="w-[24px] h-[10px] bg-zinc-800/30 rounded hidden sm:flex items-center" />
                </div>
                <div className="w-full flex flex-col space-y-2">
                  <div className="w-[34px] h-[10px] bg-zinc-800/30 rounded flex items-center" />
                  <div className="w-[21px] h-[10px] bg-zinc-800/30 rounded flex items-center" />
                  <div className="w-[10px] h-[10px] rounded-full bg-zinc-800/30 flex items-center" />
                </div>
              </div>
              <div className="h-full text-[8px] sm:text-[12px] max-h-full w-[calc(100%-64px)] sm:w-[calc(100%-80px)] max-w-[calc(100%-64px)] sm:max-w-[calc(100%-80px)] flex flex-col flex-col-reverse items-start space-y-1 font-mono whitespace-pre overflow-y-scroll overflow-hide-scrollbar overflow-x-hidden pointer-events-none py-3">
                {requests.reverse().map((val, i) => {
                  if (val === null) {
                    return null;
                  }
                  return (
                    <div className="odd:bg-zinc-800/15 w-full px-3 py-[5px] text-[8px] text-zinc-400 flex items-center" key={i}>
                      <span className="text-zinc-100/20">{val.substring(0, 10)}</span>
                      <span
                        className={["grayscale-[0.35]", val.substring(10, 13).at(0) === "2" ? "text-green-600" : val.at(0) === "4" ? "text-amber-500" : "text-red-500"].join(" ")}
                      >
                        {val.substring(10, 13)}
                      </span>
                      <span>{val.substring(13)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </React.Fragment>
      );
  }
}

function GraphicReplay() {
  const buttonId = React.useId();
  const cursorId = React.useId();

  const [state, setState] = React.useState({
    id: 0,
    dx: 0,
    dy: 0,
    clicking: false,
    copied: false,
    start: false,
    finish: true,
  });

  React.useEffect(() => {
    if (state.finish) {
      setTimeout(() => setState((prev) => ({ id: prev.id + 1, dx: 0, dy: 0, clicking: false, copied: false, finish: false, start: true })), 500);
      return;
    }
    if (state.start) {
      const id = state.id;
      const button = document.getElementById(buttonId)!;
      const cursor = document.getElementById(cursorId)!;
      const { top: bt, left: bl } = button.getBoundingClientRect();
      const { top: ct, left: cl } = cursor.getBoundingClientRect();
      const dy = bt - ct + 16;
      const dx = bl - cl + 16;
      setTimeout(() => setState((prev) => (prev.id !== id ? prev : { ...prev, start: false, dx, dy })), 500);
      setTimeout(() => setState((prev) => (prev.id !== id ? prev : { ...prev, clicking: true })), 2400);
      setTimeout(() => setState((prev) => (prev.id !== id ? prev : { ...prev, clicking: false })), 2600);
      setTimeout(() => setState((prev) => (prev.id !== id ? prev : { ...prev, copied: true })), 2700);
      setTimeout(() => setState((prev) => (prev.id !== id ? prev : { ...prev, copied: false })), 5000);
      setTimeout(() => setState((prev) => (prev.id !== id ? prev : { ...prev, finish: true, dx: 0, dy: 0 })), 5800);
    }
  }, [state, buttonId, cursorId]);

  return (
    <div className="w-full h-full relative text-zinc-300 text-[9px] sm:text-[12px]">
      <div
        className={[
          "absolute top-0 left-0 w-full h-full border-t border-zinc-800/50 rounded-lg bg-zinc-900/30 flex flex-col divide-y divide-zinc-800/30 transition-opacity duration-[300ms] ease-in-out",
          !state.finish ? "opacity-[1]" : "opacity-[0]",
        ].join(" ")}
      >
        <div className="w-full grow-0 shrink-0 p-[4px] sm:p-[6px] pl-3 pb-[0px] h-[30px] sm:h-[36px] flex items-center space-x-3">
          <div className="grow-0 shrink-0 flex items-center space-x-1 pl-2">
            <div className="w-[10px] h-[10px] rounded-full bg-zinc-800/40" />
            <div className="w-[10px] h-[10px] rounded-full bg-zinc-800/40" />
            <div className="w-[10px] h-[10px] rounded-full bg-zinc-800/40" />
          </div>
          <lucide.ArrowLeft size={14} strokeWidth={3.5} className="text-zinc-200 opacity-[0.15]" />
          <lucide.RotateCw size={14} strokeWidth={3.5} className="text-zinc-200 opacity-[0.15]" />
          <a href="/dashboard" className="grow h-[calc(100%-4px)] sm:h-full w-full rounded-md bg-zinc-800/40 flex items-center px-3 space-x-2 font-sans hover:brightness-[1.20]">
            <lucide.Globe size={11} strokeWidth={3.5} className="text-zinc-200 opacity-[0.20]" />
            <span className="font-semibold text-zinc-200/15 leading-[10px] text-[10px]">subtrace.dev/dashboard</span>
          </a>
        </div>
        <div className="w-full grow-0 shrink-0 mt-[4px] sm:mt-[2px] flex h-[calc(100%-30px)] sm:h-[calc(100%-36px)] divide-x divide-zinc-800/30">
          <div className="h-full flex flex-col justify-between pl-2 pr-8 pt-3 pb-5 w-[64px] sm:w-[80px] bg-black/15">
            <div className="w-full flex flex-col space-y-[6px] sm:space-y-2">
              <div className="w-full px-1 pb-2 rounded flex items-center">
                <LogoIcon className="w-[11px] h-[11px] text-zinc-400" />
              </div>
              <div className="w-[32px] h-[10px] bg-zinc-800/30 rounded flex items-center" />
              <div className="w-[40px] h-[10px] bg-zinc-800/30 rounded flex items-center" />
              <div className="w-[24px] h-[10px] bg-zinc-800/30 rounded hidden sm:flex items-center" />
            </div>
            <div className="w-full flex flex-col space-y-2">
              <div className="w-[34px] h-[10px] bg-zinc-800/30 rounded flex items-center" />
              <div className="w-[21px] h-[10px] bg-zinc-800/30 rounded flex items-center" />
              <div className="w-[10px] h-[10px] rounded-full bg-zinc-800/30 flex items-center" />
            </div>
          </div>
          <div className="h-full text-[8px] sm:text-[12px] max-h-full w-[calc(100%-64px)] sm:w-[calc(100%-80px)] max-w-[calc(100%-64px)] sm:max-w-[calc(100%-80px)] flex flex-col items-start font-mono whitespace-pre divide-y divide-zinc-800/30">
            <div className="z-[2] grow-0 shrink-0 h-fit w-full flex flex-col-reverse py-3">
              {["  122ms   200   GET      /api/users/me", "  788ms   200   POST     /v1/chat/completions", "  427ms   500   PUT      /api/users/me"].reverse().map((val, i) => {
                if (val === null) {
                  return null;
                }
                return (
                  <div
                    key={i}
                    className={[
                      "z-[0] w-full px-3 py-[5px] text-[8px] text-zinc-400 flex items-center border-l-[2px]",
                      i === 0 ? "border-zinc-300 bg-zinc-800/60 brightness-[1.20]" : "odd:bg-zinc-800/15 border-transparent",
                    ].join(" ")}
                  >
                    <span className="text-zinc-100/20">{val.substring(0, 10)}</span>
                    <span
                      className={["grayscale-[0.35]", val.substring(10, 13).at(0) === "2" ? "text-green-600" : val.at(0) === "4" ? "text-amber-500" : "text-red-500"].join(" ")}
                    >
                      {val.substring(10, 13)}
                    </span>
                    <span className="relative">
                      {val.substring(13)}
                      {(() => {
                        if (i !== 0) {
                          return null;
                        }
                        return (
                          <div
                            key="cursor"
                            id={cursorId}
                            style={{ transform: `translateX(${state.dx}px) translateY(${state.dy}px)` }}
                            className={[
                              "z-[2] absolute text-zinc-300 left-[50%] top-[90%]",
                              !state.finish ? "transition-transform duration-[800ms] ease-in-out" : "hidden",
                              state.clicking ? "scale-[0.95] rounded-full outline outline-[2px] outline-offset-[16px] outline-zinc-100/0" : "",
                            ].join(" ")}
                          >
                            <lucide.Pointer size={16} strokeWidth={2.5} />
                          </div>
                        );
                      })()}
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="z-[1] grow bg-zinc-800/30 w-full h-full p-2 md:p-4 pt-0 md:pt-2 flex flex-col space-y-3 overflow-hidden relative">
              <div
                id={buttonId}
                className={[
                  "cursor-pointer absolute bottom-[8px] right-[4px] md:right-[8px] px-2 md:px-3 py-1 border border-zinc-800 font-sans text-[8px] md:text-[9px] font-semibold rounded text-zinc-300/90 transition-all duration-[150ms] ease-in-out bg-zinc-800/70",
                  state.clicking ? "scale-[0.90]" : "",
                ].join(" ")}
              >
                <div className="relative flex items-center">
                  <span>Copy as cURL</span>
                  <div
                    className={[
                      "absolute top-[-4px] left-[50%] translate-x-[-50%] text-zinc-200 text-[8px] transition-all duration-[150ms] ease-out",
                      state.copied ? "translate-y-[-16px] opacity-[1]" : "translate-y-[-8px] opacity-[0]",
                    ].join(" ")}
                  >
                    Copied!
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 md:space-x-3 text-zinc-600 font-sans font-medium uppercase text-[7px]">
                  <lucide.X size={8} strokeWidth={3} />
                  <div className="text-zinc-400">General</div>
                  <div className="hidden md:block">Request</div>
                  <div className="hidden md:block">Response</div>
                  <div className="block md:hidden">Payload</div>
                  <div className="">Timing</div>
                  <div className="">HAR</div>
                </div>
              </div>
              <div className="flex flex-col space-y-[6px] pl-[16px] md:pl-[20px]">
                <div className="w-[68px]  h-[9px] rounded-sm mx-[-1px] bg-zinc-800/90" />
                <div className="w-[95px]  h-[9px] rounded-sm mx-[-1px] bg-zinc-800/50" />
                <div className="w-[144px] h-[9px] rounded-sm mx-[-1px] bg-zinc-800/50" />
                <div className="w-[82px]  h-[6px] rounded-sm mx-[-1px] bg-zinc-800/0" />
                <div className="w-[121px] h-[9px] rounded-sm mx-[-1px] bg-zinc-800/90" />
                <div className="w-[57px]  h-[9px] rounded-sm mx-[-1px] bg-zinc-800/50" />
                <div className="w-[111px] h-[9px] rounded-sm mx-[-1px] bg-zinc-800/50" />
                <div className="w-[72px]  h-[9px] rounded-sm mx-[-1px] bg-zinc-800/50" />
                <div className="w-[135px] h-[6px] rounded-sm mx-[-1px] bg-zinc-800/0" />
                <div className="w-[67px]  h-[9px] rounded-sm mx-[-1px] bg-zinc-800/90" />
                <div className="w-[89px]  h-[9px] rounded-sm mx-[-1px] bg-zinc-800/50" />
                <div className="w-[76px]  h-[9px] rounded-sm mx-[-1px] bg-zinc-800/50" />
                <div className="w-[103px] h-[9px] rounded-sm mx-[-1px] bg-zinc-800/50" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function GraphicTiming() {
  const segments: { label: string; duration: number; color: string; culprit?: boolean }[] = [
    { label: "parse_query", duration: 4, color: "bg-sky-400" },
    { label: "calc_embedding", duration: 387, color: "bg-emerald-400" },
    { label: "redis_cache_lookup", duration: 24, color: "bg-emerald-400" },
    { label: "postgres_query", duration: 31, color: "bg-emerald-400" },
    { label: "s3_fetch", duration: 4413, color: "bg-red-500", culprit: true },
    { label: "redis_cache_write", duration: 17, color: "bg-emerald-400" },
    { label: "rerank", duration: 180, color: "bg-purple-400" },
    { label: "compose_response", duration: 50, color: "bg-indigo-400" },
    { label: "outbound_write", duration: 27, color: "bg-lime-400" },
  ];

  let total = 0;
  for (let i = 0; i < segments.length; i++) {
    total += segments[i].duration;
  }

  let left = 0;
  const bars = segments.map((seg) => {
    const bar = {
      ...seg,
      leftPercent: (left / total) * 100,
      widthPercent: (seg.duration / total) * 100,
    };
    left += seg.duration;
    return bar;
  });

  return (
    <div className="w-full h-full relative text-zinc-300 text-[9px] sm:text-[12px]">
      <div
        key="browser"
        className="absolute top-0 left-0 w-full h-full border-t border-zinc-800/50 rounded-lg bg-zinc-900/30 flex flex-col divide-y divide-zinc-800/30 transition-opacity duration-[300ms] ease-in-out"
      >
        <div className="w-full grow-0 shrink-0 p-[4px] sm:p-[6px] pl-3 pb-[0px] h-[30px] sm:h-[36px] flex items-center space-x-3">
          <div className="grow-0 shrink-0 flex items-center space-x-1 pl-2">
            <div className="w-[10px] h-[10px] rounded-full bg-zinc-800/40" />
            <div className="w-[10px] h-[10px] rounded-full bg-zinc-800/40" />
            <div className="w-[10px] h-[10px] rounded-full bg-zinc-800/40" />
          </div>
          <lucide.ArrowLeft size={14} strokeWidth={3.5} className="text-zinc-200 opacity-[0.15]" />
          <lucide.RotateCw size={14} strokeWidth={3.5} className="text-zinc-200 opacity-[0.15]" />
          <a href="/dashboard" className="grow h-[calc(100%-4px)] sm:h-full w-full rounded-md bg-zinc-800/40 flex items-center px-3 space-x-2 font-sans hover:brightness-[1.20]">
            <lucide.Globe size={11} strokeWidth={3.5} className="text-zinc-200 opacity-[0.20]" />
            <span className="font-semibold text-zinc-200/15 leading-[10px] text-[10px]">subtrace.dev/dashboard</span>
          </a>
        </div>
        <div className="w-full grow-0 shrink-0 mt-[4px] sm:mt-[2px] flex h-[calc(100%-30px)] sm:h-[calc(100%-36px)] divide-x divide-zinc-800/30">
          <div className="h-full flex flex-col justify-between pl-2 pr-8 pt-3 pb-5 w-[64px] sm:w-[80px] bg-black/15">
            <div className="w-full flex flex-col space-y-[6px] sm:space-y-2">
              <div className="w-full px-1 pb-2 rounded flex items-center">
                <LogoIcon className="w-[11px] h-[11px] text-zinc-400" />
              </div>
              <div className="w-[32px] h-[10px] bg-zinc-800/30 rounded flex items-center" />
              <div className="w-[40px] h-[10px] bg-zinc-800/30 rounded flex items-center" />
              <div className="w-[24px] h-[10px] bg-zinc-800/30 rounded hidden sm:flex items-center" />
            </div>
            <div className="w-full flex flex-col space-y-2">
              <div className="w-[34px] h-[10px] bg-zinc-800/30 rounded flex items-center" />
              <div className="w-[21px] h-[10px] bg-zinc-800/30 rounded flex items-center" />
              <div className="w-[10px] h-[10px] rounded-full bg-zinc-800/30 flex items-center" />
            </div>
          </div>
          <div className="h-full text-[8px] sm:text-[12px] max-h-full w-[calc(100%-64px)] sm:w-[calc(100%-80px)] max-w-[calc(100%-64px)] sm:max-w-[calc(100%-80px)] flex flex-col items-start font-mono whitespace-pre divide-y divide-zinc-800/30">
            <div className="grow-0 shrink-0 h-fit w-full flex flex-col-reverse py-3">
              {[" 5007ms   408   GET      /api/search?q=news", " 5011ms   408   POST     /api/search?q=weather", ` ${total}ms   408   PUT      /api/search?q=stocks`]
                .reverse()
                .map((val, i) => {
                  if (val === null) {
                    return null;
                  }
                  return (
                    <div
                      key={i}
                      className={[
                        "w-full px-3 py-[5px] text-[8px] text-zinc-400 flex items-center border-l-[2px]",
                        i === 0 ? "border-zinc-300 bg-zinc-800/60 brightness-[1.20]" : "odd:bg-zinc-800/15 border-transparent",
                      ].join(" ")}
                    >
                      <span className="text-zinc-100/20">{val.substring(0, 10)}</span>
                      <span
                        className={["grayscale-[0.35]", val.substring(10, 13).at(0) === "2" ? "text-green-600" : val.at(0) === "4" ? "text-amber-500" : "text-red-500"].join(" ")}
                      >
                        {val.substring(10, 13)}
                      </span>
                      <span>{val.substring(13)}</span>
                    </div>
                  );
                })}
            </div>
            <div className="grow bg-zinc-800/30 w-full h-full p-4 pt-2 flex flex-col space-y-3 overflow-hidden">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 md:space-x-3 text-zinc-600 font-sans font-medium uppercase text-[7px]">
                  <lucide.X size={8} strokeWidth={3} />
                  <div className="">General</div>
                  <div className="hidden md:block">Request</div>
                  <div className="hidden md:block">Response</div>
                  <div className="block md:hidden">Payload</div>
                  <div className="text-zinc-400">Timing</div>
                  <div className="">HAR</div>
                </div>
              </div>
              <div className="flex flex-col pl-[16px] pl-[20px] grayscale-[0.25]">
                <div className="flex h-full items-begin md:items-center space-x-6">
                  <div
                    className="relative w-[65%] my-4 h-16 md:h-24 bg-[size:10%_10%] md:bg-[size:5%_5%]"
                    style={{
                      backgroundImage: "linear-gradient(to right, #ffffff08 1px, transparent 1px)",
                    }}
                  >
                    {bars.map((bar, i) => (
                      <div key={bar.label} className="group/bar">
                        <div
                          className={[bar.color, "absolute"].join(" ")}
                          style={{
                            left: `${0.9 * bar.leftPercent}%`,
                            width: `${0.9 * bar.widthPercent}%`,
                            top: `${(100 * i) / bars.length}%`,
                            height: `${(100 * 1) / bars.length / 2}%`,
                            marginBottom: `${(100 * 1) / bars.length / 2}%`,
                          }}
                        ></div>
                        <div
                          className="hidden group-hover/bar:flex font-normal absolute translate-x-[8px] translate-y-[-16px] text-[7px] text-zinc-300"
                          style={{
                            left: `${0.9 * bar.leftPercent}%`,
                            top: `${(100 * i) / bars.length}%`,
                          }}
                        >
                          {bar.label}
                        </div>
                      </div>
                    ))}
                  </div>
                  <ul className="w-[35%] flex flex-col space-y-[4px] text-[7.5px] text-zinc-500">
                    {bars.map((bar) => (
                      <li key={bar.label} className="flex items-center space-x-[6px]">
                        <div className={["grow-0 shrink-0 h-[3px] w-[3px] rounded-full", bar.color].join(" ")}></div>
                        <div className="grow tracking-tight truncate font-medium text-zinc-500/80 pr-2">{bar.label}</div>
                        <div className="grow-0 shrink-0 text-right text-zinc-100/20">{bar.duration}ms</div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function LandingPage(): React.ReactElement {
  const stickyRef = React.useRef<HTMLDivElement | null>(null);
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);
  const [selectedLanguage, setSelectedLanguage] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    const sticky = stickyRef.current;
    const sentinel = sentinelRef.current;
    if (!sticky || !sentinel || selectedLanguage !== undefined) {
      return;
    }

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          setTimeout(() => setSelectedLanguage((val) => (val === undefined ? "Python" : val)), 0);
          obs.disconnect();
        }
      },
      { root: null, threshold: 0 },
    );

    obs.observe(sentinel);
    return () => obs.disconnect();
  }, [selectedLanguage]);

  return (
    <div className="bg-[#09090b] xl:bg-transparent w-full flex flex-col px-4 lg:px-0 relative">
      <div className="z-[-100] absolute top-0 left-0 w-full h-full hidden xl:flex justify-center items-center">
        <div className="w-full h-full max-w-[1200px] bg-zinc-950 border-l border-r border-zinc-900"></div>
      </div>

      <div className="-mx-4 lg:mx-0 w-[calc(100%+32px)] lg:w-full flex justify-center">
        <GameOfLife />
      </div>

      <div className="z-[2] -mx-4 lg:mx-0 w-[calc(100%+32px)] lg:w-full h-[48px] flex justify-center sticky top-0">
        <div className="bg-[#09090b] h-full w-full max-w-[1200px] xl:border-x border-b border-zinc-900 flex justify-between items-center px-[36px] md:px-[72px]">
          <div className="flex justify-center items-center space-x-[80px]">
            <a href="/" className="p-1 -m-1 flex items-center space-x-1 text-zinc-200 hover:text-zinc-100 hover:brightness-[1.10]">
              <LogoIcon className="w-[16px] h-[16px]" />
              <span className="select-none font-medium font-mono text-[10px]">subtrace</span>
            </a>
            <div className="hidden lg:flex justify-center items-center text-[10px]">
              <a href="/pricing" className="flex font-mono items-center px-3 py-2 space-x-1 text-zinc-400 hover:text-zinc-200 hover:brightness-[1.10]">
                /pricing
              </a>
              <a href="/docs" className="flex font-mono items-center px-3 py-2 space-x-1 text-zinc-400 hover:text-zinc-200 hover:brightness-[1.10]">
                /docs
              </a>
              <a href="/customers" className="flex font-mono items-center px-3 py-2 space-x-1 text-zinc-400 hover:text-zinc-200 hover:brightness-[1.10]">
                /customers
              </a>
              <a href="/blog" className="flex font-mono items-center px-3 py-2 space-x-1 text-zinc-400 hover:text-zinc-200 hover:brightness-[1.10]">
                /blog
              </a>
              <a href="/discord" className="flex font-mono items-center px-3 py-2 space-x-1 text-zinc-400 hover:text-zinc-200 hover:brightness-[1.10]">
                /discord
              </a>
            </div>
          </div>
          <div className="flex justify-center items-center space-x-[10px]">
            <a
              target="_blank"
              href="https://github.com/subtrace/subtrace"
              className="h-[32px] flex justify-center items-center space-x-2 rounded bg-transparent transition-bg duration-[150ms] ease-in-out text-zinc-300 hover:text-zinc-100 hover:bg-zinc-900 px-[10px]"
            >
              <svg
                className="w-[16px] h-[16px]"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
                focusable="false"
                viewBox="0 0 24 24"
                display="inline-block"
                overflow="visible"
              >
                <path
                  fill="currentColor"
                  d="M12.5.75C6.146.75 1 5.896 1 12.25c0 5.089 3.292 9.387 7.863 10.91.575.101.79-.244.79-.546 0-.273-.014-1.178-.014-2.142-2.889.532-3.636-.704-3.866-1.35-.13-.331-.69-1.352-1.18-1.625-.402-.216-.977-.748-.014-.762.906-.014 1.553.834 1.769 1.179 1.035 1.74 2.688 1.25 3.349.948.1-.747.402-1.25.733-1.538-2.559-.287-5.232-1.279-5.232-5.678 0-1.25.445-2.285 1.178-3.09-.115-.288-.517-1.467.115-3.048 0 0 .963-.302 3.163 1.179.92-.259 1.897-.388 2.875-.388.977 0 1.955.13 2.875.388 2.2-1.495 3.162-1.179 3.162-1.179.633 1.581.23 2.76.115 3.048.733.805 1.179 1.825 1.179 3.09 0 4.413-2.688 5.39-5.247 5.678.417.36.776 1.05.776 2.128 0 1.538-.014 2.774-.014 3.162 0 .302.216.662.79.547C20.709 21.637 24 17.324 24 12.25 24 5.896 18.854.75 12.5.75Z"
                ></path>
              </svg>
              <div className="leading-[12px] text-[11px] pt-[1px] font-medium">2.7K</div>
            </a>
            <a
              href="/login"
              className="max-w-fit whitespace-pre group/button px-4 h-[32px] flex justify-center items-center space-x-2 rounded w-full text-xs font-medium bg-zinc-100 hover:bg-zinc-50 cursor-pointer text-zinc-700 active:scale-[0.95]"
            >
              <div>Start</div>
              <div className="duration-[100ms] translate-x-[-1px] group-hover/button:translate-x-[+1px]">&rarr;</div>
            </a>
          </div>
        </div>
      </div>

      <div className="relative">
        <div className="z-[3] -mx-4 lg:mx-0 w-[calc(100%+32px)] lg:w-full h-[48px] flex justify-center absolute top-[-48px]">
          <div className="bg-black h-full w-full max-w-[1200px] xl:border-x border-b border-zinc-900 flex items-center px-[36px] md:px-[72px] group/logos relative overflow-hidden">
            <div className="w-full flex justify-center items-center space-x-4 md:space-x-6 lg:space-x-8 scale-[0.90] xl:scale-[1.00] grayscale brightness-[0.20]">
              <img key="hinge" src={HingeHealthLogo} className="h-[21px] hidden sm:block lg:block" />
              <img key="adobe" src={AdobeLogo} className="grayscale h-[18px] block sm:block lg:block" />
              <img key="crea" src={CreaLogo} className="h-[24px] hidden sm:hidden lg:block" />
              <img key="trieve" src={TrieveLogo} className="h-[40px] block sm:block lg:block" />
              <img key="datch" src={DatchLogo} className="h-[16px] hidden sm:block lg:block" />
              <img key="spectrocloud" src={SpectrocloudLogo} className="h-[36px] invert hidden sm:hidden lg:block" />
              <img key="aetna" src={AetnaLogo} className="h-[14px] hidden sm:hidden lg:block" />
              <img key="method" src={MethodLogo} className="h-[21px] block sm:block lg:block" />
              <img key="pointer" src={PointerLogo} className="h-[15px] invert block sm:block lg:block" />
            </div>
            <div className="absolute top-0 left-0 w-full h-full transition-all delay-[500ms] duration-[200ms] ease-in-out blur-[8px] group-hover/logos:blur-[0px] scale-[1.15] group-hover/logos:scale-[1] flex opacity-[0] group-hover/logos:opacity-[1] select-none ">
              <div className="w-full bg-black/95 flex justify-center items-center whitespace-pre text-zinc-300 font-medium text-[11px]">
                <span>With developer signups from high performance teams worldwide</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="z-[0]">
        <div ref={sentinelRef} className="w-full h-[1px]" />
        <div ref={stickyRef} className="z-[1] -mx-4 lg:mx-0 w-[calc(100%+32px)] lg:w-full hidden sm:flex justify-center sticky top-[48px]">
          <div className="py-4 h-full w-full bg-[#09090b] max-w-[1200px] xl:border-x border-b border-zinc-900 flex flex-col space-y-8 px-[36px] md:px-[72px]">
            <Supports
              selectedLanguage={selectedLanguage}
              setSelectedLanguage={setSelectedLanguage}
              logos={[
                { name: "Python", icon: PythonLogo },
                { name: "Node", icon: NodejsLogo },
                { name: "Go", icon: GoLogo },
                { name: "Rust", icon: RustLogo },
                { name: "PHP", icon: PhpLogo },
                { name: "Ruby", icon: RubyLogo },
              ]}
            />
          </div>
        </div>

        <div className="w-full h-[64px]" />

        <div className="-mx-4 lg:mx-0 w-[calc(100%+32px)] lg:w-full flex justify-center">
          <div className="z-[0] h-full w-full max-w-[1200px] flex flex-col items-center px-[20px] md:px-[72px] py-6 md:py-12 space-y-4">
            <div className="pb-8">
              <a
                href="/blog/http2-preface"
                className="px-4 py-2 w-fit rounded bg-zinc-900/50 border border-zinc-900 text-zinc-300/60 text-[10px] font-medium hover:brightness-[1.20] flex items-center space-x-2"
              >
                <lucide.Scroll className="text-zinc-300" size={10} strokeWidth={2} />
                <span className="text-zinc-300">NEW BLOG</span>
                <span />
                <span>HOW HTTP/2 PREFACE WORKS</span>
                <lucide.ArrowRight size={10} strokeWidth={2} />
              </a>
            </div>
            <div className="text-[24px] sm:text-[32px] lg:text-[40px] font-semibold text-zinc-300 text-center">
              {/* See everything in your infrastructure */}
              {/* See everything, debug anything */}
              {/* Get to know your production */}
              {/* Frictionless production debugging */}
              Inspect element for your infra
            </div>
            <div className="w-full sm:max-w-[32rem] text-zinc-500 text-[15px] text-center">
              Subtrace is a network monitoring tool that shows you every HTTP request in your production infrastructure without any code changes.
            </div>
          </div>
        </div>

        <div className="-mx-4 lg:mx-0 w-[calc(100%+32px)] lg:w-full flex justify-center">
          <div className="z-[0] h-full w-full max-w-[1200px] -flex flex-col px-[20px] md:px-[72px] space-y-32 py-24">
            <div className="w-full flex max-w-[36rem] lg:max-w-full flex-col lg:flex-row lg:justify-between items-start space-y-8 lg:space-y-0 lg:space-x-4 group/container">
              <div className="grow flex flex-col space-y-3">
                <div className="grayscale-[0.30] opacity-[0.175] space-x-[4px] flex items-center">
                  <lucide.Radar size={18} strokeWidth={2.5} className="text-sky-500 group-hover/container:animate-[jump_0.50s_linear_0.00s]" />
                  <lucide.Plus size={18} strokeWidth={2.5} className="text-zinc-300/50 group-hover/container:animate-[jump_0.50s_linear_0.10s]" />
                  <lucide.Zap size={18} strokeWidth={2.5} className="text-amber-400 group-hover/container:animate-[jump_0.50s_linear_0.20s]" />
                </div>
                <div className="text-zinc-300 font-medium text-[17px]">Drop-in omniscience</div>
                <div className="text-zinc-500 text-[14px] w-full max-w-[21rem]">With just one command, you're instantly wired into every request in your infra in realtime.</div>
              </div>
              <div className="ph-no-capture lg:!mt-[32px] grow-0 shrink-0 aspect-[4/3] md:aspect-[16/9] w-full lg:max-w-[min(55%,600px)] overflow-hidden">
                <div className="w-full h-full relative text-zinc-300 text-[9px] sm:text-[12px]">
                  <GraphicOmniscience selectedLanguage={selectedLanguage} />
                </div>
              </div>
            </div>

            <div className="w-full flex max-w-[36rem] lg:max-w-full flex-col lg:flex-row lg:justify-between items-start space-y-8 lg:space-y-0 lg:space-x-4 group/container">
              <div className="grow flex flex-col space-y-3">
                <div className="grayscale-[0.30] opacity-[0.175] space-x-[4px] flex items-center">
                  <lucide.Skull size={18} strokeWidth={2.5} className="text-red-300 group-hover/container:animate-[jump_0.50s_linear_0.00s]" />
                  <lucide.Plus size={18} strokeWidth={2.5} className="text-zinc-300/50 group-hover/container:animate-[jump_0.50s_linear_0.10s]" />
                  <lucide.History size={18} strokeWidth={2.5} className="text-cyan-300 group-hover/container:animate-[jump_0.50s_linear_0.20s]" />
                </div>
                <div className="text-zinc-300 font-medium text-[17px]">Time travel replay</div>
                <div className="text-zinc-500 text-[14px] w-full max-w-[21rem]">Copy any prod request and replay it locally with headers and body intact.</div>
              </div>
              <div className="ph-no-capture lg:!mt-[32px] grow-0 shrink-0 aspect-[4/3] md:aspect-[16/9] w-full lg:max-w-[min(55%,600px)] overflow-hidden">
                <GraphicReplay />
              </div>
            </div>

            <div className="w-full flex max-w-[36rem] lg:max-w-full flex-col lg:flex-row lg:justify-between items-start space-y-8 lg:space-y-0 lg:space-x-4 group/container">
              <div className="grow flex flex-col space-y-3">
                <div className="grayscale-[0.30] opacity-[0.175] space-x-[4px] flex items-center">
                  <lucide.Turtle size={18} strokeWidth={2.5} className="text-emerald-400 group-hover/container:animate-[jump_0.50s_linear_0.00s]" />
                  <lucide.Plus size={18} strokeWidth={2.5} className="text-zinc-300/50 group-hover/container:animate-[jump_0.50s_linear_0.10s]" />
                  <div className="group-hover/container:animate-[jump_0.50s_linear_0.20s]">
                    <lucide.LampDesk size={18} strokeWidth={2.5} className="scale-x-[-1] text-yellow-500" />
                  </div>
                </div>
                <div className="text-zinc-300 font-medium text-[17px]">Understand your p99</div>
                <div className="text-zinc-500 text-[14px] w-full max-w-[360px]">Go beyond just sanitized averages and see the latency breakdown of every individual request.</div>
              </div>
              <div className="ph-no-capture lg:!mt-[32px] grow-0 shrink-0 aspect-[4/3] md:aspect-[16/9] w-full lg:max-w-[min(55%,600px)] overflow-hidden">
                <GraphicTiming />
              </div>
            </div>

            <div className="hidden w-full flex max-w-[36rem] lg:max-w-full flex-col lg:flex-row lg:justify-between items-start space-y-8 lg:space-y-0 lg:space-x-4 group/container">
              <div className="grow flex flex-col space-y-3">
                <div className="grayscale-[0.40] opacity-[0.20] space-x-[8px] flex items-center">
                  <lucide.Plug size={18} strokeWidth={2.5} className="text-purple-400 group-hover/container:animate-[jump_0.50s_linear_0.00s]" />
                  <lucide.TriangleAlert size={18} strokeWidth={2.5} className="text-yellow-400 group-hover/container:animate-[jump_0.50s_linear_0.10s]" />
                  <lucide.Crosshair size={18} strokeWidth={2.5} className="text-blue-400 group-hover/container:animate-[jump_0.50s_linear_0.20s]" />
                </div>
                <div className="text-zinc-300 font-medium text-[17px]">Debug third-party outages</div>
                <div className="text-zinc-500 text-[14px] w-full max-w-[360px]">
                  When an upstream dependency has issues, scope the blast radius, see which of your users are affected, and get to a workaround faster.
                </div>
              </div>
              <div className="ph-no-capture lg:!mt-[32px] grow-0 shrink-0 aspect-[16/9] w-full lg:max-w-[min(55%,600px)] bg-zinc-900/30 border border-zinc-900 rounded-lg"></div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-48 flex justify-center">
        <div className="w-full max-w-[32rem] flex flex-col items-center rounded-lg overflow-hidden outline outline-zinc-800/80 outline-[1px] divide divide-y divide-zinc-800/80">
          <a
            className="bg-zinc-900/85 hover:bg-zinc-900/70 px-8 py-6 flex flex-col space-y-6"
            href="https://x.com/skeptrune/status/1879072724796244194"
            target="_blank"
            rel="noreferrer"
          >
            <div className="text-zinc-300 text-sm flex flex-col space-y-2">
              <p>
                I never want to build anything without
                <span> </span>
                <a target="_blank" className="hover:brightness-[1.20]" href="https://x.com/subtrace_dev" rel="noreferrer">
                  @subtrace_dev
                </a>
                <span> </span>
                configured locally ever again. Life changing piece of software.
              </p>
            </div>
            <div className="flex justify-between items-center">
              <div className="flex justify-center item-center space-x-2 text-xs">
                <img className="mx-[2px] w-4 h-4 rounded-full" src={NickProfilePicture} />
                <a href="https://x.com/skeptrune" target="_blank" className="text-zinc-400/80 hover:brightness-[1.2] font-semibold" rel="noreferrer">
                  Nick Khami
                </a>
                <span className="text-zinc-500/80 font-medium">CEO, Trieve (YC W24)</span>
              </div>
              <img className="h-3 w-3 invert opacity-[0.50]" src={XLogo} />
            </div>
          </a>
          <div className="w-full relative group px-8 py-6 flex justify-between items-center space-x-3 md:space-x-6 bg-black">
            <img src={TrieveLogo} className="w-[4rem] md:w-[5rem] grayscale opacity-[0.15] brightness-[3.00] ml-auto" />
            <a
              href="/customers/trieve"
              className="min-w-fit max-w-fit px-4 py-2 duration-[100ms] group/button flex justify-center items-center space-x-2 rounded w-full text-xs font-medium bg-zinc-900/85 outline outline-[1px] outline-zinc-800 hover:brightness-[0.90] cursor-pointer text-zinc-200"
            >
              <div>Read full case study</div>
              <div className="duration-[100ms] translate-x-[-1px] group-hover/button:translate-x-[+1px]">&rarr;</div>
            </a>
          </div>
        </div>
      </div>

      <div className="mt-48 flex justify-center">
        <div className="flex flex-col space-y-[6px] md:space-y-4 max-w-[100%]">
          <div className="w-full flex items-center text-[14px] md:text-[16px] font-medium">
            <div className="relative overflow-hidden w-full h-96 px-6 py-4 bg-zinc-900/70 rounded-xl">
              <span className="text-zinc-300">Supports every language.</span>
              <span> </span>
              <span className="text-zinc-500">Finally, automatic tracing that just works. For everyone.</span>
              <IntegrationsOld />
            </div>
          </div>

          <div className="-mx-[6px] md:-mx-2 grid grid-cols-2 md:grid-cols-4 text-[13px] md:text-[14px] justify-items-center">
            <div className="mx-[6px] md:mx-2 my-[6px] md:my-0 max-w-48 aspect-square overflow-hidden p-4 md:p-5 bg-zinc-900/80 rounded-xl">
              <span className="font-medium text-zinc-200">Predictable pricing.</span>
              <span> </span>
              <span className="font-normal text-zinc-500">Flat and simple pricing that anyone can understand. No surprises.</span>
            </div>
            <div className="mx-[6px] md:mx-2 my-[6px] md:my-0 max-w-48 aspect-square overflow-hidden p-4 md:p-5 bg-zinc-900/80 rounded-xl">
              <span className="font-medium text-zinc-200">Built on ClickHouse.</span>
              <span> </span>
              <span className="font-normal text-zinc-500">Search across millions of requests in just milliseconds using the fastest database ever.</span>
            </div>
            <div className="mx-[6px] md:mx-2 my-[6px] md:my-0 max-w-48 aspect-square overflow-hidden p-4 md:p-5 bg-zinc-900/80 rounded-xl">
              <span className="font-medium text-zinc-200">No code changes.</span>
              <span> </span>
              <span className="font-normal text-zinc-500">Keep your code clean with fully automatic instrumentation.</span>
            </div>
            <div className="mx-[6px] md:mx-2 my-[6px] md:my-0 max-w-48 aspect-square overflow-hidden p-4 md:p-5 bg-zinc-900/80 rounded-xl">
              <span className="font-medium text-zinc-200">Always open source.</span>
              <span> </span>
              <span className="text-zinc-500">Avoid vendor lock-in.</span>
              <span> </span>
              <a target="_blank" href="https://github.com/subtrace/subtrace" className="text-zinc-500 hover:text-zinc-300">
                Star the GitHub repo
              </a>
              <span> </span>
              <span className="text-zinc-500">to support us.</span>
            </div>
          </div>

          <div className="flex px-3 md:px-5 py-[4px] md:py-2 bg-zinc-900/80 rounded-xl">
            <div className="w-full flex flex-col space-y-1 md:flex-row md:space-y-0 items-center md:justify-between py-2 md:py-0">
              <div className="text-[17px] md:text-[14px] font-medium py-4 md:py-0 flex flex-col md:flex-row space-x-[2px] items-center text-zinc-100">
                <span className="text-zinc-200">Want to debug faster?</span>
                <span> </span>
                <span className="text-zinc-500 hidden md:block">Start using Subtrace.</span>
              </div>
              <a
                href="/login"
                className="rounded-lg md:mb-0 bg-zinc-800/35 md:bg-zinc-800/50 hover:brightness-[0.90] group md:-mx-3 px-8 py-4 md:py-[10px] w-full md:w-[calc(25%-16px)] text-[15px] md:text-[14px] text-center flex flex-col space-y-[4px]"
                onClick={() => {
                  TelemetryUtils.log({ type: "clickLogin_landingPage_startForFree" });
                }}
              >
                <div className="text-zinc-200 font-medium flex justify-center flex-nowrap">
                  <span className="text-nowrap">Start for free</span>
                  <span className="inline-block ml-2 transition ease-in-out translate-x-[-1px] group-hover:translate-x-[+1px]">&rarr;</span>
                </div>
                <div className="text-zinc-600/60 text-[10px] font-medium md:hidden">No credit card required</div>
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full h-32 xl:h-48" />

      <Footer />
    </div>
  );
}
